/**
 * storeDetector.js
 *
 * Detects the platform / install source of the current user so we can route
 * them to the correct app-store review page.
 *
 * Detection priority (highest to lowest):
 *   1. Cached value in localStorage (once we detect a source we keep it).
 *   2. URL query parameter `?src=huawei|google|ios|web` (used in marketing
 *      links from each store landing page).
 *   3. document.referrer (App gallery / Play store deep-link referrers).
 *   4. User-Agent sniffing (HarmonyOS / HMSCore / Android / iOS).
 *   5. Fallback: web.
 */

const STORAGE_KEY = 'falak.installSource';

export const SOURCES = {
  HUAWEI: 'huawei',
  GOOGLE: 'google',
  IOS: 'ios',
  WEB: 'web',
};

export const STORE_URLS = {
  huawei: 'https://appgallery.huawei.com/app/C118150847',
  google: 'https://play.google.com/store/apps/details?id=host.emergent.daylight_sync.twa',
  ios: null, // Not released yet
};

function detectFromUA() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';

  // iOS detection (iPhone / iPad / iPod)
  if (/iPad|iPhone|iPod/i.test(ua) && !window.MSStream) return SOURCES.IOS;

  // Huawei devices (HarmonyOS or Android on Huawei without GMS)
  if (/HuaweiBrowser|HMSCore|HarmonyOS|; HUAWEI |; HONOR /i.test(ua)) {
    return SOURCES.HUAWEI;
  }

  // Generic Android device — assume Google Play (installed via TWA).
  if (/Android/i.test(ua)) return SOURCES.GOOGLE;

  return SOURCES.WEB;
}

/**
 * Returns the detected install source. Result is cached in localStorage
 * once the app has been opened as a TWA / installed PWA.
 */
export function detectSource() {
  if (typeof window === 'undefined') return SOURCES.WEB;

  // 0. TWA launch referrer (android-app://<package>) — most reliable, and
  //    overrides any wrong value cached by older builds.
  const launchRef = (typeof document !== 'undefined' && document.referrer) || '';
  const fromPkg = /android-app:\/\/host\.emergent\.daylight_sync\.huawei/i.test(launchRef)
    ? SOURCES.HUAWEI
    : /android-app:\/\/host\.emergent\.daylight_sync\.twa/i.test(launchRef)
      ? SOURCES.GOOGLE
      : null;
  if (fromPkg) {
    try { localStorage.setItem(STORAGE_KEY, fromPkg); } catch (_) {}
    return fromPkg;
  }

  // 1. Cached value
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached && Object.values(SOURCES).includes(cached)) return cached;
  } catch (_) {}

  // 2. URL param override (?src=huawei)
  try {
    const params = new URLSearchParams(window.location.search);
    const src = (params.get('src') || params.get('source') || '').toLowerCase();
    if (Object.values(SOURCES).includes(src)) {
      try { localStorage.setItem(STORAGE_KEY, src); } catch (_) {}
      return src;
    }
  } catch (_) {}

  // 3. Referrer-based detection
  const ref = (typeof document !== 'undefined' && document.referrer) || '';
  if (/appgallery\.huawei\.com|hispace\.hicloud/i.test(ref)) {
    try { localStorage.setItem(STORAGE_KEY, SOURCES.HUAWEI); } catch (_) {}
    return SOURCES.HUAWEI;
  }
  if (/play\.google\.com/i.test(ref)) {
    try { localStorage.setItem(STORAGE_KEY, SOURCES.GOOGLE); } catch (_) {}
    return SOURCES.GOOGLE;
  }

  // 4. Standalone (installed PWA / TWA) + UA-based
  const isStandalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;

  const uaSource = detectFromUA();

  // Only cache the detection if the app is actually installed (TWA context),
  // to avoid locking a web user into a store URL they can't use.
  if (isStandalone) {
    try { localStorage.setItem(STORAGE_KEY, uaSource); } catch (_) {}
  }
  return uaSource;
}

export function getStoreUrl(source) {
  return STORE_URLS[source] || null;
}

// Android package names (per /public/.well-known/assetlinks.json). Used only
// for native intent deep-links to open the store app directly on device.
const PKG = {
  google: 'host.emergent.daylight_sync.twa',
  huawei: 'host.emergent.daylight_sync.huawei',
};

/**
 * Opens the store's review page for the given source. On Android the
 * `market://` intent is tried first (opens Play Store natively), falling
 * back to the https URL.
 */
export function openStoreReview(source) {
  const url = getStoreUrl(source);
  if (!url) return false;

  // Android native scheme fallback (works for TWA)
  if (source === SOURCES.GOOGLE) {
    try {
      window.location.href = `market://details?id=${PKG.google}`;
      setTimeout(() => { window.open(url, '_blank', 'noopener'); }, 600);
      return true;
    } catch (_) {}
  }
  if (source === SOURCES.HUAWEI) {
    try {
      window.location.href = `appmarket://details?id=${PKG.huawei}`;
      setTimeout(() => { window.open(url, '_blank', 'noopener'); }, 600);
      return true;
    } catch (_) {}
  }

  window.open(url, '_blank', 'noopener');
  return true;
}
